import { useMemo, useState } from 'react'
import { useMqttTopic } from '../mqtt.jsx'
import { useSelection } from '../selection.jsx'
import { sortSessionCodes, sessionLabel, sessionIsScored } from '../sessions.js'
import { fmtSessionTime, gateColumns, gateValueFor } from '../format.js'

const EMPTY_CLASSES = []

export default function ComparePage() {
  const results = useMqttTopic('results')
  const venue = useMqttTopic('venue')
  const sessionOverrides = useMqttTopic('sessions')?.overrides
  const { selected, toggle, clear } = useSelection()
  const [pickedSession, setPickedSession] = useState(null)

  const classes = results?.classes ?? EMPTY_CLASSES
  const allRiders = useMemo(() => classes.flatMap((c) => c.competitors.map((r) => ({ ...r, className: c.className }))), [classes])
  const chosen = allRiders.filter((r) => selected.has(r.num))

  const sessionCodes = useMemo(() => {
    const codes = new Set()
    chosen.forEach((r) => Object.keys(r.sessions || {}).forEach((code) => codes.add(code)))
    return sortSessionCodes([...codes])
  }, [chosen])

  // Same derive-don't-sync pattern as ResultsPage - see the comment there.
  const session =
    pickedSession && sessionCodes.includes(pickedSession) ? pickedSession : sessionCodes[sessionCodes.length - 1]

  if (selected.size === 0) {
    return (
      <div>
        <div className="section-head">
          <h2>Compare</h2>
          <p>Select riders on Results or All Runs to line them up here.</p>
        </div>
        <div className="empty-state">Nothing selected yet.</div>
      </div>
    )
  }

  const best = chosen.reduce((min, r) => {
    const t = r.sessions?.[session]?.time
    return t && (min == null || t < min) ? t : min
  }, null)

  const cols = gateColumns(chosen, session, venue?.splits)
  // Times (type T) are best when lowest; speed-trap readings (type S) are
  // best when highest - a speed isn't "faster" for being a smaller number.
  const bestAtField = Object.fromEntries(
    cols.map((c) => [
      c.field,
      chosen.reduce((bestVal, r) => {
        const v = gateValueFor(r.sessions?.[session], c.field)
        if (v == null) return bestVal
        if (bestVal == null) return v
        return c.type === 'S' ? Math.max(bestVal, v) : Math.min(bestVal, v)
      }, null),
    ]),
  )

  return (
    <div>
      <div className="section-head">
        <h2>Compare</h2>
        <p>
          {selected.size} rider{selected.size === 1 ? '' : 's'} selected —{' '}
          <button
            onClick={clear}
            style={{ background: 'none', border: 'none', color: 'var(--gulf-blue)', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}
          >
            clear
          </button>
        </p>
      </div>

      {sessionCodes.length > 0 && (
        <div className="toolbar">
          <div className="session-switch">
            {sessionCodes.map((code) => (
              <button
                key={code}
                className={`chip ${sessionIsScored(code, sessionOverrides) ? 'timed' : ''} ${session === code ? 'active' : ''}`}
                onClick={() => setPickedSession(code)}
              >
                <span className="code">{code}</span> {sessionLabel(code, sessionOverrides)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="compare-grid">
        {chosen.map((r) => {
          const t = fmtSessionTime(r.sessions?.[session])
          const isBest = t.value != null && best != null && t.value <= best + 0.001
          const delta = t.value != null && best != null && t.value > best + 0.001 ? `+${(t.value - best).toFixed(2)}` : null
          return (
            <div className="rider-card" key={r.num}>
              <header>
                <div>
                  <div className="name">{r.rider}</div>
                  <div className="machine">{r.machine}</div>
                </div>
                <div className="num mono">#{r.num}</div>
              </header>
              {cols.map((c) => {
                const v = gateValueFor(r.sessions?.[session], c.field)
                const bestForField = bestAtField[c.field]
                const isBestGate =
                  v != null &&
                  bestForField != null &&
                  (c.type === 'S' ? v >= bestForField - 0.001 : v <= bestForField + 0.001)
                // Speed deltas (v - best) come out negative already (slower
                // than the best speed), so toFixed's own sign is enough -
                // times need an explicit '+' since they're always slower.
                const gateDelta =
                  v != null && bestForField != null && !isBestGate
                    ? c.type === 'S'
                      ? (v - bestForField).toFixed(1)
                      : `+${(v - bestForField).toFixed(2)}`
                    : null
                return (
                  <div className="split-row" key={c.field}>
                    <span className="k">{c.label}</span>
                    <span className={`v ${isBestGate ? 'purple' : ''}`}>
                      {/* === 'S' not === 'T' - NIGAN's Finish-beam gate (type 'F')
                          duplicates the run's finish time and must render like one
                          (2dp), not fall into the 1dp branch meant for speed traps. */}
                      {v != null ? v.toFixed(c.type === 'S' ? 1 : 2) : '·'}
                      {gateDelta && <span className="delta">{gateDelta}</span>}
                    </span>
                  </div>
                )
              })}
              <div className="split-row finish-row">
                <span className="k">{session ? sessionLabel(session, sessionOverrides) : 'Time'}</span>
                <span className={`v ${isBest ? 'purple' : ''}`}>
                  {t.text}
                  {delta && <span className="delta">{delta}</span>}
                </span>
              </div>
              <button
                onClick={() => toggle(r.num)}
                style={{ marginTop: 10, width: '100%', background: 'none', border: '1px dashed var(--line)', borderRadius: 7, padding: '6px 0', fontSize: '0.74rem', color: 'var(--text-dim)', cursor: 'pointer' }}
              >
                Remove
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
